// Console capture core functionality
import { InitOptions } from './types';

// Cast window to avoid TypeScript conflicts
const globalWindow = window as any;

const DEFAULT_SUPPRESS_PATTERNS = [
  'Support for defaultProps will be removed from memo components',
  'react-beautiful-dnd',
  'Unable to find draggable with id',
];

export function initConsoleCapture(options: InitOptions = {}) {
  const { suppressPatterns = DEFAULT_SUPPRESS_PATTERNS, maxLogs = 1000 } = options;

  // Only initialize if not already done
  if (!globalWindow.consoleCapture || !globalWindow.consoleCapture.initialized) {
    globalWindow.consoleCapture = {
      logs: [],
      originalMethods: {},
      hasErrors: false,
      initialized: true,
      suppressedCount: 0,
    };

    // Enhanced browser warning capture
    const initBrowserWarningCapture = () => {
      // Capture browser security warnings and violations
      if ('ReportingObserver' in window) {
        try {
          const observer = new ReportingObserver(reports => {
            reports.forEach(report => {
              const body = report.body as any; // Type assertion for broader compatibility
              const warningEntry = {
                type: 'BROWSER_WARNING',
                timestamp: new Date().toISOString(),
                message: `Browser Warning: ${report.type} - ${body?.message || body?.sourceFile || 'Unknown'}`,
                category: 'system',
                rawArgs: [report],
                reportType: report.type,
                sourceFile: body?.sourceFile,
                lineNumber: body?.lineNumber,
                columnNumber: body?.columnNumber,
              };

              globalWindow.consoleCapture.logs.push(warningEntry);
              globalWindow.consoleCapture.hasErrors = true;

              // Also log to original console for debugging
              globalWindow.consoleCapture.originalMethods.warn(
                'FDC captured browser warning:',
                report
              );
            });
          });

          observer.observe();
          console.log('🔍 FetchDevConsole: Browser warning capture enabled');
        } catch (error) {
          console.log('⚠️ FetchDevConsole: Browser warning capture not supported');
        }
      }

      // Capture Content Security Policy violations
      window.addEventListener('securitypolicyviolation', event => {
        const violationEntry = {
          type: 'CSP_VIOLATION',
          timestamp: new Date().toISOString(),
          message: `CSP Violation: ${event.violatedDirective} - ${event.blockedURI}`,
          category: 'network',
          rawArgs: [event],
          violatedDirective: event.violatedDirective,
          blockedURI: event.blockedURI,
          sourceFile: event.sourceFile,
          lineNumber: event.lineNumber,
          columnNumber: event.columnNumber,
        };

        globalWindow.consoleCapture.logs.push(violationEntry);
        globalWindow.consoleCapture.hasErrors = true;

        // Also log to original console
        globalWindow.consoleCapture.originalMethods.error('FDC captured CSP violation:', event);
      });
    };

    // Initialize browser warning capture
    initBrowserWarningCapture();

    // Initialize global error handling
    const initGlobalErrorHandling = () => {
      // Global error handler for JavaScript errors
      window.addEventListener('error', event => {
        globalWindow.consoleCapture.hasErrors = true;

        const errorDetails = {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error ? event.error.toString() : null,
          stack: event.error ? event.error.stack : null,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        };

        // Use original console to avoid loops
        globalWindow.consoleCapture.originalMethods.error(
          'FetchDevConsole - Global JavaScript Error:',
          errorDetails
        );
      });

      // Handle unhandled promise rejections
      window.addEventListener('unhandledrejection', function (event) {
        globalWindow.consoleCapture.hasErrors = true;
        // Use original console to avoid loops
        globalWindow.consoleCapture.originalMethods.error(
          'FetchDevConsole - Unhandled promise rejection:',
          event.reason
        );
      });
    };

    initGlobalErrorHandling(); // Store and override console methods only once
    (['log', 'error', 'warn', 'info', 'debug'] as const).forEach(method => {
      globalWindow.consoleCapture.originalMethods[method] = console[method];
      console[method] = function (...args: any[]) {
        // Suppress known third-party warnings
        if (method === 'error' || method === 'warn') {
          const message = args[0];
          if (typeof message === 'string') {
            // Check against suppress patterns
            const shouldSuppress = suppressPatterns.some(pattern => message.includes(pattern));
            if (shouldSuppress) {
              globalWindow.consoleCapture.suppressedCount =
                (globalWindow.consoleCapture.suppressedCount || 0) + 1;
              return; // Suppress these warnings completely
            }
          }
        }

        // Call original method first
        globalWindow.consoleCapture.originalMethods[method].apply(console, args);

        // Track if we have errors
        if (method === 'error') {
          globalWindow.consoleCapture.hasErrors = true;
        }

        // Store the output with enhanced detail
        const logEntry = {
          type: method.toUpperCase(),
          timestamp: new Date().toISOString(),
          message: args
            .map(arg => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
            .join(' '),
          category: method === 'error' || method === 'warn' ? 'error' : 'system',
          // Enhanced details for better debugging
          rawArgs: args.map(arg => {
            if (arg instanceof Error) {
              return {
                type: 'Error',
                name: arg.name,
                message: arg.message,
                stack: arg.stack,
                fileName: (arg as any).fileName,
                lineNumber: (arg as any).lineNumber,
                columnNumber: (arg as any).columnNumber,
                // Capture Axios-specific error details
                code: (arg as any).code,
                status: (arg as any).status,
                statusText: (arg as any).statusText,
                config: (arg as any).config
                  ? {
                      url: (arg as any).config.url,
                      method: (arg as any).config.method,
                      baseURL: (arg as any).config.baseURL,
                      headers: (arg as any).config.headers,
                    }
                  : undefined,
                response: (arg as any).response
                  ? {
                      status: (arg as any).response.status,
                      statusText: (arg as any).response.statusText,
                      data: (arg as any).response.data,
                    }
                  : undefined,
              };
            } else if (typeof arg === 'object' && arg !== null) {
              try {
                // Check if it's an Axios error-like object
                if (arg.isAxiosError || (arg.response && arg.config)) {
                  return {
                    type: 'AxiosError',
                    message: arg.message,
                    code: arg.code,
                    status: arg.status,
                    statusText: arg.statusText,
                    isAxiosError: arg.isAxiosError,
                    config: arg.config
                      ? {
                          url: arg.config.url,
                          method: arg.config.method,
                          baseURL: arg.config.baseURL,
                        }
                      : undefined,
                    response: arg.response
                      ? {
                          status: arg.response.status,
                          statusText: arg.response.statusText,
                          data:
                            typeof arg.response.data === 'string'
                              ? arg.response.data
                              : JSON.stringify(arg.response.data),
                        }
                      : undefined,
                    stack: arg.stack,
                  };
                }
                return JSON.parse(JSON.stringify(arg));
              } catch {
                return String(arg);
              }
            }
            return arg;
          }),
          // Capture stack trace for errors and warnings
          stackTrace: method === 'error' || method === 'warn' ? new Error().stack : undefined,
          // Browser info
          userAgent: navigator.userAgent,
          url: window.location.href,
        };

        globalWindow.consoleCapture.logs.push(logEntry);

        // Limit stored logs to prevent memory issues
        if (globalWindow.consoleCapture.logs.length > maxLogs) {
          globalWindow.consoleCapture.logs = globalWindow.consoleCapture.logs.slice(-maxLogs);
        }
      };
    });
  }

  // Make test functions available globally for debugging
  globalWindow.testConsoleCapture = function () {
    console.log('Testing FetchDevConsole system...');
    console.warn('This is a test warning');
    console.error('This is a test error to trigger the copy button');
    console.info('FetchDevConsole test completed');
    return 'Test console messages generated';
  };

  // Debug logging function for application-specific debug messages
  globalWindow.debugLog = function (message: any, ...additionalArgs: any[]) {
    // Call original console.log method
    globalWindow.consoleCapture.originalMethods.log.apply(console, [message, ...additionalArgs]);

    // Store as debug category
    globalWindow.consoleCapture.logs.push({
      type: 'DEBUG',
      timestamp: new Date().toISOString(),
      message: [message, ...additionalArgs]
        .map(arg => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
        .join(' '),
      category: 'debug',
    });

    // Limit stored logs to prevent memory issues
    if (globalWindow.consoleCapture.logs.length > (options.maxLogs || 1000)) {
      globalWindow.consoleCapture.logs = globalWindow.consoleCapture.logs.slice(
        -(options.maxLogs || 1000)
      );
    }
  };

  // Copy function with URL info and filtering options
  globalWindow.copyAllConsole = async function (errorsOnly = false) {
    const currentUrl = window.location.href;
    const referrer = document.referrer || 'Direct navigation';
    const timestamp = new Date().toISOString();

    // Filter logs based on preference (errors only or all)
    const filteredLogs = errorsOnly
      ? globalWindow.consoleCapture.logs.filter(
          (log: any) => log.type === 'ERROR' || log.type === 'WARN'
        )
      : globalWindow.consoleCapture.logs;

    // Create header with context information
    const header = `=== CONSOLE OUTPUT CAPTURED BY FETCHDEVCONSOLE ===
This console was copied from: ${currentUrl}
Referrer page: ${referrer}
Captured at: ${timestamp}
Filter: ${errorsOnly ? 'Errors and Warnings Only' : 'All Console Messages'}
Total messages: ${filteredLogs.length}
${errorsOnly ? `(${globalWindow.consoleCapture.logs.length - filteredLogs.length} other messages filtered out)` : ''}
=======================================

`;

    const logOutput = filteredLogs
      .map((log: any) => {
        let output = `[${log.timestamp}] ${log.type}: ${log.message}`;

        // Add enhanced details for errors and warnings
        if ((log.type === 'ERROR' || log.type === 'WARN') && log.rawArgs) {
          const errorDetails = log.rawArgs.find(
            (arg: any) => arg && typeof arg === 'object' && arg.type === 'Error'
          );
          if (errorDetails) {
            output += `\n  Error Details:`;
            output += `\n    Name: ${errorDetails.name}`;
            if (errorDetails.fileName)
              output += `\n    File: ${errorDetails.fileName}:${errorDetails.lineNumber}:${errorDetails.columnNumber}`;
            if (errorDetails.stack) output += `\n    Stack: ${errorDetails.stack}`;
          }

          // Add stack trace if available
          if (log.stackTrace) {
            output += `\n  Call Stack:\n${log.stackTrace}`;
          }
        }

        return output;
      })
      .join('\n\n');

    const output = header + logOutput;

    try {
      await navigator.clipboard.writeText(output);
      // Use original console method to avoid potential loops
      globalWindow.consoleCapture.originalMethods.log(
        'FetchDevConsole: Console output copied to clipboard!'
      );

      // Show user feedback
      const message = errorsOnly
        ? `${filteredLogs.length} errors/warnings copied to clipboard!`
        : `${filteredLogs.length} console messages copied to clipboard!`;

      if (globalWindow.showConsoleAlert) {
        globalWindow.showConsoleAlert(message);
      } else if (typeof alert !== 'undefined') {
        alert(message);
      }
    } catch (err) {
      globalWindow.consoleCapture.originalMethods.error('FetchDevConsole copy failed:', err);
      if (typeof alert !== 'undefined') {
        alert('Failed to copy console output');
      }
    }
  };

  // Debug function to investigate discrepancies
  globalWindow.debugConsoleCapture = function () {
    const stats = {
      captured: globalWindow.consoleCapture.logs.length,
      errors: globalWindow.consoleCapture.logs.filter((log: any) => log.type === 'ERROR').length,
      warnings: globalWindow.consoleCapture.logs.filter((log: any) => log.type === 'WARN').length,
      suppressed: globalWindow.consoleCapture.suppressedCount || 0,
    };

    globalWindow.consoleCapture.originalMethods.log('🐛 FetchDevConsole Debug Info:', {
      ...stats,
      recentErrorLogs: globalWindow.consoleCapture.logs
        .filter((log: any) => log.type === 'ERROR')
        .slice(-3)
        .map((log: any) => ({
          message: log.message,
          hasStackTrace: !!log.stackTrace,
          hasRawArgs: !!log.rawArgs,
          timestamp: log.timestamp,
        })),
      recentWarningLogs: globalWindow.consoleCapture.logs
        .filter((log: any) => log.type === 'WARN')
        .slice(-3)
        .map((log: any) => ({
          message: log.message,
          hasStackTrace: !!log.stackTrace,
          hasRawArgs: !!log.rawArgs,
          timestamp: log.timestamp,
        })),
      suppressPatterns: suppressPatterns,
      allLogTypes: Array.from(
        new Set(globalWindow.consoleCapture.logs.map((log: any) => log.type))
      ),
      enhancedLoggingEnabled: true,
    });

    // Also log to regular console to compare
    console.log('🔍 Enhanced logging now captures stack traces and detailed error info');
    console.log(
      '📊 FetchDevConsole should now show detailed error information similar to browser console'
    );

    return stats;
  };

  // Quick function to compare counts
  globalWindow.compareConsoleCounts = function () {
    globalWindow.consoleCapture.originalMethods.log('=== CONSOLE COUNT COMPARISON ===');
    globalWindow.consoleCapture.originalMethods.log('📊 FetchDevConsole captured:', {
      total: globalWindow.consoleCapture.logs.length,
      errors: globalWindow.consoleCapture.logs.filter((log: any) => log.type === 'ERROR').length,
      warnings: globalWindow.consoleCapture.logs.filter((log: any) => log.type === 'WARN').length,
      suppressed: globalWindow.consoleCapture.suppressedCount || 0,
    });
    globalWindow.consoleCapture.originalMethods.log(
      '💡 Check browser DevTools console for comparison'
    );
    globalWindow.consoleCapture.originalMethods.log(
      '🚨 If you see more errors in DevTools than FetchDevConsole shows, they may be:'
    );
    globalWindow.consoleCapture.originalMethods.log(
      '   - Errors that occurred before FetchDevConsole initialized'
    );
    globalWindow.consoleCapture.originalMethods.log('   - Errors from browser extensions');
    globalWindow.consoleCapture.originalMethods.log(
      '   - Errors suppressed by these patterns:',
      suppressPatterns
    );
  };

  // Global error handler for JavaScript errors
  window.addEventListener('error', event => {
    globalWindow.consoleCapture.hasErrors = true;

    const errorDetails = {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
        ? {
            name: event.error.name,
            message: event.error.message,
            stack: event.error.stack,
            toString: event.error.toString(),
          }
        : null,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      type: 'GLOBAL_ERROR',
    };

    // Store in our capture system with enhanced format
    globalWindow.consoleCapture.logs.push({
      type: 'ERROR',
      timestamp: new Date().toISOString(),
      message: `Global JavaScript Error: ${event.message}`,
      category: 'error',
      rawArgs: [errorDetails],
      stackTrace: event.error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    // Use original console to avoid loops
    globalWindow.consoleCapture.originalMethods.error(
      'FetchDevConsole - Global JavaScript Error:',
      errorDetails
    );
  });

  // Enhanced promise rejection handler
  window.addEventListener('unhandledrejection', event => {
    globalWindow.consoleCapture.hasErrors = true;

    const rejectionDetails = {
      reason: event.reason,
      reasonType: typeof event.reason,
      reasonString: String(event.reason),
      promise: '[Promise object]',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      type: 'PROMISE_REJECTION',
    };

    // Try to extract more info from reason if it's an error
    if (event.reason instanceof Error) {
      rejectionDetails.reason = {
        name: event.reason.name,
        message: event.reason.message,
        stack: event.reason.stack,
      };
    }

    // Store in our capture system
    globalWindow.consoleCapture.logs.push({
      type: 'ERROR',
      timestamp: new Date().toISOString(),
      message: `Unhandled Promise Rejection: ${event.reason?.message || String(event.reason)}`,
      category: 'error',
      rawArgs: [rejectionDetails],
      stackTrace: event.reason?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    // Use original console to avoid loops
    globalWindow.consoleCapture.originalMethods.error(
      'FetchDevConsole - Unhandled Promise Rejection:',
      rejectionDetails
    );
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', function (event) {
    globalWindow.consoleCapture.hasErrors = true;
    // Use original console to avoid loops
    globalWindow.consoleCapture.originalMethods.error(
      'FetchDevConsole - Unhandled promise rejection:',
      event.reason
    );
  });
}

// Utility function to test console capture
export function testConsoleCapture(): string {
  const globalWindow = window as any;
  if (globalWindow.testConsoleCapture) {
    return globalWindow.testConsoleCapture();
  }
  throw new Error('FetchDevConsole not initialized. Call initConsoleCapture() first.');
}

// Utility function for debug logging
export function debugLog(message: any, ...additionalArgs: any[]): void {
  const globalWindow = window as any;
  if (globalWindow.debugLog) {
    globalWindow.debugLog(message, ...additionalArgs);
  } else {
    console.log('[DEBUG]', message, ...additionalArgs);
  }
}

// Get recent errors from console capture (for feedback submission)
export function getRecentErrors(maxMinutesAgo: number = 5): any[] {
  const globalWindow = window as any;
  if (!globalWindow.consoleCapture?.logs) {
    return [];
  }

  const cutoffTime = new Date(Date.now() - maxMinutesAgo * 60 * 1000);

  return globalWindow.consoleCapture.logs
    .filter((log: any) => {
      if (log.type !== 'ERROR' && log.type !== 'WARN') return false;
      const logTime = new Date(log.timestamp);
      return logTime > cutoffTime;
    })
    .slice(-10) // Limit to last 10 errors
    .map((log: any) => ({
      level: log.type.toLowerCase(),
      message: log.message,
      timestamp: log.timestamp,
      url: log.url,
    }));
}
